import { canUseDOM } from "exenv";
import FuzzySearch from "fuzzy-search";
import { createRoot } from "react-dom/client";

import { customEmojis, fetchCustomEmojis } from "./fetchCustomEmojis";
import emojiMap from "./generated/emoji.json";
import { isCustomEmoji } from "./isCustomEmoji";
import type { EmojiItem } from "./types";

import type { AutoCompletion, CompletionItem, Token } from "~/client/components/shared/editor/completions";

interface Item extends EmojiItem, CompletionItem {
  isCustom: boolean;
}

export class EmojiCompletion implements AutoCompletion<Item> {
  emojiSearch: FuzzySearch;

  readonly empty = { text: ":" };

  // Fetch and store custom emoji data before auto completion, except SSR with hypernova.
  constructor() {
    if (canUseDOM) {
      fetchCustomEmojis();
    }
  }

  shouldTriggerComplete(token: Token) {
    return token.type !== "comment";
  }

  async search(term: string) {
    if (!this.emojiSearch) {
      const emojis: Array<Item> = Object.keys(emojiMap).map((name) => {
        const emoji = emojiMap[name];
        return {
          text: `${name} `, // replacement
          name,
          src: emoji,
          isCustom: isCustomEmoji(emoji),
        };
      });

      const customs = customEmojis().map((item) => {
        return {
          text: `:${item.name}: `,
          name: `:${item.name}:`,
          src: item.src,
          isCustom: true,
        };
      });

      // Unicode絵文字とカスタム絵文字の名前が被っていたときはカスタム絵文字を優先する
      const filteredEmojis = emojis.filter((emoji) => {
        return !customs.some((custom) => custom.name === emoji.name);
      });

      this.emojiSearch = new FuzzySearch(customs.concat(filteredEmojis), ["name"], { sort: true });
    }

    if (term === ":") {
      return [this.empty, ...this.emojiSearch.haystack];
    } else {
      return this.emojiSearch.search(term);
    }
  }

  component(emoji: Item) {
    if (!emoji.name) {
      return null;
    }

    return (
      <div className="completion-item">
        {emoji.isCustom ? (
          <img className="icon" alt="" src={emoji.src} />
        ) : (
          // is 属性が指定されている className 属性が動作しなくなるため、class 属性を使用する
          // https://github.com/facebook/react/issues/4933
          // @ts-expect-error
          <span class="icon unicode-emoji" is="k-unicode-emoji" title={emoji.name}>
            {emoji.src}
          </span>
        )}
        <span className="name">{emoji.name}</span>
      </div>
    );
  }

  render(container, emoji: Item) {
    const component = this.component(emoji);
    if (component === null) {
      return;
    }

    const root = createRoot(container);
    root.render(component);
  }
}
